/**
 * Sidebar menu opened from the topbar hamburger (mobile and desktop).
 */
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';

import {
  InlineTextButton,
  LinkedLogo,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import { ConsoleNavItem } from '../siteNavLinks';
import css from './TopbarMobileMenu.module.css';

/**
 * Menu for mobile and desktop layouts (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} sidebar menu
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const user = ensureCurrentUser(currentUser);

  const consoleLinks = (customLinks || []).map((linkConfig, index) => (
    <ConsoleNavItem
      key={`${linkConfig.text}_${index}`}
      linkConfig={linkConfig}
      currentPage={currentPage}
      itemClassName={css.navItem}
      currentItemClassName={css.currentPage}
    />
  ));

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const searchLink = (
    <li className={classNames(css.navItem, currentPageClass('SearchPage'))}>
      <NamedLink name="SearchPage">
        <FormattedMessage id="TopbarDesktop.search" />
      </NamedLink>
    </li>
  );

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  const accountSection = isAuthenticated ? (
    <>
      <p className={css.greeting}>
        <FormattedMessage
          id="TopbarMobileMenu.greeting"
          values={{ displayName: user.attributes.profile.firstName }}
        />
      </p>
      <ul className={css.linkList}>
        {/* !commit because inbox is already visible in the topbar mobile in right side */}
        {/* <li className={classNames(css.navItem, currentPageClass(`InboxPage:${inboxTab}`))}>
          <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCount > 0 ? (
              <NotificationBadge className={css.notificationBadge} count={notificationCount} />
            ) : null}
          </NamedLink>
        </li> */}
        {showCreateListingsLink ? (
          <li className={classNames(css.navItem, currentPageClass('ManageListingsPage'))}>
            <NamedLink name="ManageListingsPage">
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          </li>
        ) : null}
        <li className={classNames(css.navItem, currentPageClass('FavoritesPage'))}>
          <NamedLink name="FavoritesPage">
            <FormattedMessage id="TopbarMobileMenu.savedLink" />
          </NamedLink>
        </li>
        <li className={classNames(css.navItem, currentPageClass('ProfileSettingsPage'))}>
          <NamedLink name="ProfileSettingsPage">
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
        </li>
        <li className={classNames(css.navItem, currentPageClass('AccountSettingsPage'))}>
          <NamedLink name="AccountSettingsPage">
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </li>
        <li className={css.navItem}>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarMobileMenu.logoutLink" />
          </InlineTextButton>
        </li>
      </ul>
    </>
  ) : (
    <ul className={css.linkList}>
      <li className={css.navItem}>
        <NamedLink name="LoginPage">
          <FormattedMessage id="TopbarDesktop.login" />
        </NamedLink>
      </li>
      <li className={css.navItem}>
        <NamedLink name="SignupPage">
          <FormattedMessage id="TopbarDesktop.signup" />
        </NamedLink>
      </li>
    </ul>
  );

  return (
    <nav className={css.root}>
      <div className={css.header}>
        <LinkedLogo
          layout="desktop"
          className={css.logo}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
      </div>
      <div className={css.body}>
        <ul className={css.linkList}>
          {searchLink}
          {consoleLinks}
        </ul>
        <div className={css.divider} />
        {accountSection}
      </div>
      {createListingsLinkMaybe ? <div className={css.footer}>{createListingsLinkMaybe}</div> : null}
    </nav>
  );
};

export default TopbarMobileMenu;
